import { type ComponentPropsWithoutRef, forwardRef, memo } from 'react';
import { CheckSmallIcon } from '@/icons';
import { StaticRichTextRenderer } from '@/shared/features/rich-text';
import { cn } from '@/utils';
import { QuizImage } from './QuizImage';

/**
 * QuizMultipleChoice - 객관식 컴포넌트
 * EditableMultiple 구조와 일치하는 읽기 전용 버전
 * @module QuizMultipleChoice
 */

/**
 * 객관식 목록을 위한 컨테이너
 * EditableMultiple 컨테이너 구조와 일치
 */
const Container = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('flex flex-col gap-sm', className)} {...rest}>
        {children}
      </div>;
});
Container.displayName = 'QuizMultipleChoiceContainer';

/**
 * IndicatorContainer - 객관식 선택지 표시를 위한 원형 인디케이터
 * 사용자 답안 상태(정답/오답/미선택)를 시각적으로 표시
 */
const IndicatorContainer = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('flex size-[30px] flex-shrink-0 items-center justify-center rounded-full border-[3px] border-white shadow-xs', className)} {...props} data-sentry-component="IndicatorContainer" data-sentry-source-file="QuizMultipleChoice.tsx">
      {children}
    </div>;
};

/**
 * Item - Compound Component Pattern for Multiple Choice Item Layout
 * 공통 레이아웃 구조를 제공하는 복합 컴포넌트
 */
const ItemRoot = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('flex w-full flex-col gap-2xs', className)} {...rest}>
        {children}
      </div>;
});
ItemRoot.displayName = 'QuizMultipleChoiceItemRoot';
const ItemRow = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('relative flex flex-row items-center gap-xs', className)} {...rest}>
        {children}
      </div>;
});
ItemRow.displayName = 'QuizMultipleChoiceItemRow';
const ItemContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('relative flex min-h-px min-w-px grow basis-0 flex-col items-start justify-center gap-2xs', className)} {...rest}>
        {children}
      </div>;
});
ItemContent.displayName = 'QuizMultipleChoiceItemContent';

/**
 * ItemImageContainer - ml-[39px] offset과 QuizImage.Container를 결합한 컨테이너
 * QuizImage.Container의 모든 기능(hover, click)을 유지하면서 레이아웃 offset 제공
 */
const ItemImageContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('ml-[39px]', className)} {...rest}>
      <QuizImage.Container>{children}</QuizImage.Container>
    </div>;
});
ItemImageContainer.displayName = 'QuizMultipleChoiceItemImageContainer';
const Indicator = {
  Container: IndicatorContainer
};
const Image = {
  Container: ItemImageContainer,
  Thumbnail: QuizImage.Thumbnail
};
const ItemCompound = Object.assign(ItemRoot, {
  Row: ItemRow,
  Indicator,
  Content: ItemContent,
  Image
});

/**
 * Item - Props 기반 래퍼
 * QuizItemCard 등에서 사용하는 기본 API
 */
const Item = memo(forwardRef<HTMLDivElement, {
  text: string;
  imageUrl?: string;
  isAnswer: boolean;
  choiceNumber?: number;
} & ComponentPropsWithoutRef<'div'>>((props, ref) => {
  const {
    text,
    imageUrl,
    isAnswer,
    choiceNumber,
    ...rest
  } = props;
  return <ItemRoot ref={ref} {...rest}>
        <ItemRow>
          <IndicatorContainer className={isAnswer ? 'bg-green-500' : 'bg-gray-200'}>
            {isAnswer && <CheckSmallIcon size="lg" fill="white" />}
          </IndicatorContainer>
          <ItemContent>
            <StaticRichTextRenderer as="div" text={text} className="text-heading-sm-bold" />
          </ItemContent>
        </ItemRow>
        {imageUrl && <ItemImageContainer className="size-[62px]">
            <QuizImage.Thumbnail src={imageUrl} alt={`Choice ${choiceNumber}`} />
          </ItemImageContainer>}
      </ItemRoot>;
}));
Item.displayName = 'QuizMultipleChoiceItem';
export const QuizMultipleChoice = {
  Container,
  Item,
  ItemCompound
};